import * as React from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'

import { Box, SelectOption } from '@factorialco/deprecated-design-system'

import Select from 'modules/core/components/Inputs/Select'
import i18n from 'modules/core/lib/i18n'

import BlankSlate from 'modules/reports/components/BlankSlate'
import Chart from 'modules/reports/components/Chart'
import type { ReportEditorStore } from 'modules/reports/lib/Store'
import Report from 'modules/reports/store/models/Report'
import type { ResourceData } from 'modules/reports/store/models/Resource'

interface Props {
  store: ReportEditorStore
  results: ResourceData
}

const Graphs = ({ store, results }: Props) => {
  const chartTypes = [
    {
      value: Report.CHART_TYPES.table,
      label: i18n.t('reports.sql_editor.table'),
    },
    {
      value: Report.CHART_TYPES.bars,
      label: i18n.t('reports.sql_editor.bars'),
    },
    {
      value: Report.CHART_TYPES.pie,
      label: i18n.t('reports.sql_editor.pie'),
    },
    {
      value: Report.CHART_TYPES.numeric_metric,
      label: i18n.t('reports.sql_editor.numeric_metric'),
    },
    {
      value: Report.CHART_TYPES.area,
      label: i18n.t('reports.sql_editor.area'),
    },
    {
      value: Report.CHART_TYPES.lines,
      label: i18n.t('reports.sql_editor.lines'),
    },
  ]

  const { reportStruct, reportForm } = store
  const chartType = reportForm.get('chart_type').value

  const valueIsAlreadySelectedInOtherSelectors = (currentField: string, value: string): boolean => {
    const chartSchema = Report.CHARTS_SCHEMA?.[chartType]

    const keysToScan = Object.keys(chartSchema?.params ?? {}).filter(
      (param) => param !== currentField
    )

    return keysToScan.some((param) => {
      const formValue = reportForm.get(param).value

      return formValue === value
    })
  }

  const filterOptionsByType = (formField: string) => {
    const chartSchema = Report.CHARTS_SCHEMA?.[chartType]
    const fieldSchema = chartSchema?.params?.[formField]

    if (!fieldSchema) {
      return []
    }

    return Object.entries(results.rows[0] ?? {})
      .filter((entry) => {
        const value = entry[1]
        const type = typeof value

        return fieldSchema.includes(type) // && !alreadySelected
      })
      .map((entry) => ({
        value: entry[0],
        label: entry[0],
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }

  const getSelectOptions = () => {
    const chartSchema = Report.CHARTS_SCHEMA?.[chartType]

    if (chartSchema) {
      return Object.keys(chartSchema.params).reduce<Record<string, SelectOption[]>>((acc, key) => {
        const options = filterOptionsByType(key)

        return { ...acc, [key]: options }
      }, {})
    }

    return {}
  }

  // REVIEW: move this function to Report model?
  const validateChartFormData = (selectOptions: Record<string, SelectOption[]>) => {
    const chartSchema = Report.CHARTS_SCHEMA?.[chartType]

    // 1. Validate that the selected chart is supported
    // (if is included in the schema)
    if (!chartSchema) {
      return {
        error: i18n.t('reports.sql_editor.chart_not_supported'),
      }
    }

    // 2. Validate that the number of dimensions is correct
    const hasWrongNumberOfDimensions =
      Object.keys(results.rows[0] ?? {}).length < Object.keys(chartSchema.requiredParams).length

    if (hasWrongNumberOfDimensions) {
      return {
        error: i18n.t('reports.sql_editor.errors.number_of_dimensions', {
          columns: Object.keys(chartSchema.requiredParams).length,
        }),
      }
    }

    // 3. Validate that there is at least one numeric data if the chart type is not a table
    if (chartType !== Report.CHART_TYPES.table) {
      const hasNoNumericData = !Object.values(results.rows[0] ?? {}).some(
        (key: any) => typeof key === 'number'
      )

      if (hasNoNumericData) {
        return {
          error: i18n.t('reports.sql_editor.errors.no_numeric_data'),
        }
      }
    }

    // 4. Validate that the are no empty selectors in the required fields
    const hasDimensionTypesErrors = chartSchema.requiredParams.some((param) => {
      return selectOptions[param]?.length === 0
    })

    if (hasDimensionTypesErrors) {
      return {
        error: i18n.t('reports.sql_editor.errors.dimension_types'),
      }
    }
  }

  const isAllowedParameter = <T extends string>(
    selectOptions: Record<string, SelectOption[]>,
    option: T
  ): selectOptions is {
    [K in T]: SelectOption[]
  } =>
    Boolean(
      !chartFormDataValidation?.error &&
        Report.CHARTS_SCHEMA?.[chartType]?.params?.[option] &&
        (selectOptions[option]?.length ?? 0) > 0
    )

  const setFormValues = () => {
    for (const [field, options] of Object.entries(selectOptions)) {
      const formField = reportForm.get(field)

      // We need to ensure that the current form field value exists in the options
      // Otherwise, we need to reset it (if the SELECT params change, the selected value
      // needs to be reset)
      if (
        (formField.value && options.find((option) => option.value === formField.value)) ||
        (!formField.value && !Report.CHARTS_SCHEMA[chartType]?.requiredParams.includes(field))
      ) {
        continue
      }

      // Fill the form field with the first non selected option
      const selectableOption = options.find(
        (option) => !valueIsAlreadySelectedInOtherSelectors(field, option.value)
      )
      formField.set(selectableOption?.value)
    }
  }

  /** Select possible options for initial and secondary data. */
  const selectOptions = getSelectOptions()
  const chartFormDataValidation = validateChartFormData(selectOptions)
  setFormValues()

  return (
    <Box paddingBottom="s32" flexDirection="column" background="grey000White" height="full">
      <Box flexDirection="row" gap="s16">
        <Box width="s240">
          <Select
            label={i18n.t('reports.sql_editor.chart_type')}
            options={chartTypes}
            field={reportForm.get('chart_type')}
            onChange={() => setFormValues()}
          />
        </Box>

        {isAllowedParameter(selectOptions, 'initial_data') && (
          <Box width="s240">
            <Select
              label={i18n.t('reports.sql_editor.initial_data')}
              options={selectOptions.initial_data}
              field={reportForm.get('initial_data')}
            />
          </Box>
        )}

        {isAllowedParameter(selectOptions, 'secondary_data') && (
          <Box width="s240">
            <Select
              label={i18n.t('reports.sql_editor.secondary_data')}
              options={selectOptions.secondary_data}
              field={reportForm.get('secondary_data')}
            />
          </Box>
        )}

        {/* eslint-disable indent */}
        {/*
         Disabling ESLint rule because if incompatibilities between
         prettier-standard (lint-staged) and ESLint
        */}
        {isAllowedParameter(selectOptions, 'tertiary_data') &&
          selectOptions.tertiary_data.length > 2 && (
            <Box width="s240">
              <Select
                none={i18n.t('common.select_default')}
                label={i18n.t('reports.sql_editor.group_data')}
                options={selectOptions.tertiary_data}
                field={reportForm.get('tertiary_data')}
              />
            </Box>
          )}
      </Box>
      {/* eslint-enable indent */}

      <div
        style={{
          flex: '1 1 auto',
        }}
      >
        <AutoSizer disableWidth>
          {({ height }: any) => (
            <Box
              paddingTop="s32"
              style={{
                height: height,
              }}
              overflowY={chartType === Report.CHART_TYPES.table ? 'auto' : 'visible'}
            >
              {!chartFormDataValidation?.error ? (
                <Chart
                  data={results}
                  reportStruct={reportStruct}
                  showLegend
                  reduceMargins={false}
                  reportForm={reportForm}
                />
              ) : (
                <BlankSlate message={chartFormDataValidation?.error} illustration="alert" />
              )}
            </Box>
          )}
        </AutoSizer>
      </div>
    </Box>
  )
}

export default Graphs
